import { Injectable, inject } from '@angular/core';
import { uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AssetResponse, ParkAsset } from '@fpr/core/models/asset.models';
import { Dictionary } from '@fpr/core/models/utility.models';
import { VasService } from './vas.service';
import { LocaleService } from './locale.service';

export enum AssetType {
    AVATARS = 'avatars',
    EXPERIENCES = 'experiences',
    PARKS = 'parks',
    RESERVATIONS = 'reservations'
}

@Injectable({
    providedIn: 'root'
})
export class AssetManager {
    private readonly vasService = inject(VasService);
    private readonly localeService = inject(LocaleService);


    private readonly ASSET_MAP_SESSION_NAME = 'fpr.assets';
    private assets: Dictionary<any>;

    constructor() {
        const localeService = this.localeService;

        this.assets = JSON.parse(sessionStorage.getItem(this.ASSET_MAP_SESSION_NAME)) || {};

        if (this.assets.$locale !== localeService.locale) {
            this.assets = {
                $locale: localeService.locale
            };
            this.setAssetsMapSession();
        }
    }

    getAssets<T>(ids: string[], type: AssetType): Observable<Dictionary<T>> {

        ids = uniq(ids);

        const isPark = (asset: unknown): asset is ParkAsset => {
            return asset && type === AssetType.PARKS;
        };

        const idsToFetch = ids.filter(id => this.assets[id] ? false : true);

        if (idsToFetch.length) {
            return this.vasService
                .get<AssetResponse<any>>(`/v1/assets/${type}/${idsToFetch.join(',')}?locale=${this.localeService.locale}`)
                .pipe(
                    tap(assetsResponse => {

                        const assetMap = assetsResponse.assets?.reduce((acc, asset) => {
                            if (isPark(asset) && !asset.shortName) {
                                asset.shortName = asset.name;
                            }
                            acc[asset.id] = asset;

                            return acc;
                        }, {});

                        this.assets = { ...this.assets, ...assetMap };
                        this.setAssetsMapSession();

                    }),
                    map(_ => this.getAssetsFromMap(ids))
                );
        } else {
            return of(this.getAssetsFromMap(ids));
        }

    }

    private getAssetsFromMap(ids: string[]): Dictionary<any> {
        return Object.keys(this.assets)
            .filter(id => ids.includes(id))
            .reduce((acc, id) => {
                acc[id] = this.assets[id];

                return acc;
            }, {});
    }

    private setAssetsMapSession(): void {
        sessionStorage.setItem(this.ASSET_MAP_SESSION_NAME, JSON.stringify(this.assets));
    }

}
